import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import Pagination from '../components/UI/Pagination';
import { backendUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';

const BACKEND = backendUrl;

const fmtDate = d => {
    try { const x = new Date(d); return `${x.toLocaleDateString()} ${x.toLocaleTimeString()}`; } catch { return '–'; }
};
const fmtPrice = pricing => `${Number(pricing?.totalPrice ?? 0)} ${(pricing?.currency || 'credits') === 'credits' ? 'cr' : pricing?.currency}`;

const STATUS_STYLE = {
    completed: { bg: 'rgba(0,200,83,0.12)', border: 'rgba(0,200,83,0.3)',  color: '#4ade80' },
    playing:   { bg: 'rgba(0,212,255,0.12)', border: 'rgba(0,212,255,0.3)', color: '#00d4ff' },
};

const StatusBadge = ({ status }) => {
    const s = STATUS_STYLE[status] || STATUS_STYLE.playing;
    return (
        <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, border: `1px solid ${s.border}`, color: s.color }}>
            {status}
        </span>
    );
};

export default function GameList() {
    const { isLoggedIn, isAdmin, loading: authLoading } = useAuth();

    const [games, setGames]       = useState([]);
    const [loading, setLoading]   = useState(true);   // first load only — later fetches keep the table mounted
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError]       = useState(null);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 10 });
    const [statusFilter, setStatusFilter] = useState('all');

    const historyEndpoint = isAdmin
        ? `${BACKEND}/api/history/all`
        : `${BACKEND}/api/history/my`;

    const fetchGames = useCallback(async (page = 1, limit = pagination.limit, status = statusFilter) => {
        if (!isLoggedIn) return;
        setRefreshing(true); setError(null);
        try {
            const params = { page, limit };
            if (status === 'completed') params.completed = true;
            if (status === 'running')   params.completed = false;

            const res = await axios.get(historyEndpoint, { params });
            const data = res.data.data ?? res.data.games ?? [];
            const pag  = res.data.pagination ?? { total: data.length, page, limit, pages: Math.ceil(data.length / limit) };
            setGames(data);
            setPagination({ page: pag.page, pages: pag.pages, total: pag.total, limit: pag.limit });
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load games');
        } finally { setRefreshing(false); setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, pagination.limit, isLoggedIn, isAdmin]);

    useEffect(() => {
        if (authLoading) return;
        if (isLoggedIn) fetchGames(1, pagination.limit, statusFilter);
        else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isLoggedIn, statusFilter]);

    if (authLoading) return null;
    if (!isLoggedIn) return <Navigate to="/auth/login" replace />;

    const C = {
        card:   'rgba(13,19,50,0.65)',
        border: 'rgba(255,255,255,0.08)',
        cyan:   '#00d4ff',
        amber:  '#ffb300',
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="pt-24 pb-24 md:pb-8 px-4 md:pl-20"
            style={{ maxWidth: 1100, marginLeft: 'auto', marginRight: 'auto' }}
        >
            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: 0 }}>🎮 Game List</h1>
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, margin: '4px 0 0' }}>
                        {isAdmin ? 'All games' : 'My games'} — {pagination.total} total
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        style={{ background: C.card, color: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer', outline: 'none' }}
                    >
                        <option value="all">All Status</option>
                        <option value="running">In Progress</option>
                        <option value="completed">Completed</option>
                    </select>
                    <button
                        onClick={() => fetchGames(pagination.page, pagination.limit)}
                        disabled={refreshing}
                        style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.7)', cursor: refreshing ? 'wait' : 'pointer', opacity: refreshing ? 0.6 : 1 }}
                    >{refreshing ? '… Refreshing' : '↻ Refresh'}</button>
                </div>
            </div>

            {error && (
                <div style={{ padding: '12px 18px', borderRadius: 12, background: 'rgba(255,59,48,0.1)', border: '1px solid rgba(255,59,48,0.3)', color: '#ff6b6b', marginBottom: 18, fontSize: 13 }}>
                    ⚠ {error}
                </div>
            )}

            <div style={{ background: C.card, backdropFilter: 'blur(14px)', border: `1px solid ${C.border}`, borderRadius: 18, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: '80px 0', textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                        <div style={{ width: 36, height: 36, border: '2px solid rgba(0,212,255,0.2)', borderTop: `2px solid ${C.cyan}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                        Loading games…
                    </div>
                ) : games.length === 0 ? (
                    <div style={{ padding: '80px 0', textAlign: 'center' }}>
                        <div style={{ fontSize: 40, opacity: 0.25, marginBottom: 14 }}>🎲</div>
                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 14 }}>No games found</div>
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto', opacity: refreshing ? 0.55 : 1, transition: 'opacity 0.2s' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                        {['Room', 'Players', 'Password', 'Price', 'Status', 'Winner', 'Created'].map((h) => (
                                            <th key={h} style={{
                                                padding: '12px 16px', textAlign: 'left',
                                                fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)',
                                                textTransform: 'uppercase', letterSpacing: 1, background: 'rgba(0,0,0,0.1)',
                                                whiteSpace: 'nowrap',
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {games.map((g, idx) => (
                                        <tr
                                            key={g._id || g.roomId}
                                            style={{
                                                borderBottom: `1px solid rgba(255,255,255,0.04)`,
                                                background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
                                                transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                                            onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'}
                                        >
                                            <td style={{ padding: '11px 16px' }}>
                                                {g.roomCode && (
                                                    <div style={{ fontFamily: 'monospace', fontSize: 12, color: C.amber, fontWeight: 700, marginBottom: 2 }}>
                                                        {g.roomCode}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{g.roomId}</div>
                                            </td>
                                            <td style={{ padding: '11px 16px', fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                                                {g.maxPlayers ? `${g.maxPlayers}P` : '—'}
                                                <div style={{ marginTop: 4, color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
                                                    {(g.players || []).map(p => p.name || p).filter(Boolean).join(', ')}
                                                </div>
                                            </td>
                                            <td style={{ padding: '11px 16px' }}>
                                                {g.password
                                                    ? <span style={{ fontSize: 11, color: 'rgba(255,179,0,0.8)', fontWeight: 600 }}>🔒 Password set</span>
                                                    : <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>None</span>
                                                }
                                            </td>
                                            <td style={{ padding: '11px 16px', fontSize: 11, color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap' }}>
                                                {fmtPrice(g.pricing)}
                                            </td>
                                            <td style={{ padding: '11px 16px' }}>
                                                <StatusBadge status={g.completed ? 'completed' : 'playing'} />
                                            </td>
                                            <td style={{ padding: '11px 16px', fontSize: 11 }}>
                                                {g.winner
                                                    ? <span style={{ color: C.amber, fontWeight: 700 }}>🏆 {g.winner}</span>
                                                    : <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>}
                                            </td>
                                            <td style={{ padding: '11px 16px', fontSize: 11, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
                                                {fmtDate(g.createdAt)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}` }}>
                            <Pagination
                                page={pagination.page}
                                pages={pagination.pages}
                                total={pagination.total}
                                limit={pagination.limit}
                                onPage={p => fetchGames(p, pagination.limit)}
                                onLimit={l => { setPagination(prev => ({ ...prev, limit: l })); fetchGames(1, l); }}
                            />
                        </div>
                    </>
                )}
            </div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </motion.div>
    );
}
